import React from 'react';
import { 
    Box, 
    Typography, 
    Button, 
    Stack, 
    Paper, 
    Divider,
    Chip,
    alpha,
    useTheme
} from '@mui/material';
import { 
    AddTask as AddTaskIcon, 
    Lightbulb as TipIcon,
    PriorityHigh as PriorityIcon,
    Warning as ConflictIcon,
    PlayArrow as ActionIcon
} from '@mui/icons-material';

interface LynaInsightRendererProps {
    content: string;
    onApplyAction?: (type: string, data: any) => void;
}

const LynaInsightRenderer: React.FC<LynaInsightRendererProps> = ({ content, onApplyAction }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    // Regex to find [COMMAND: {JSON}]
    const commandRegex = /\[([A-Z_]+):\s*({.*?})\]/g;

    const parseContent = (text: string) => {
        const parts = [];
        let lastIndex = 0;
        let match;

        while ((match = commandRegex.exec(text)) !== null) {
            // Add text before the command
            if (match.index > lastIndex) {
                parts.push({
                    type: 'text',
                    content: text.substring(lastIndex, match.index)
                });
            }

            // Add the command
            try {
                parts.push({
                    type: 'command',
                    command: match[1],
                    data: JSON.parse(match[2])
                });
            } catch (e) {
                console.error('Error parsing command JSON:', e);
                parts.push({
                    type: 'text',
                    content: match[0]
                });
            }

            lastIndex = commandRegex.lastIndex;
        }

        if (lastIndex < text.length) {
            parts.push({
                type: 'text',
                content: text.substring(lastIndex)
            });
        }

        return parts;
    };

    const renderText = (text: string) => {
        // Simple markdown-ish rendering for **bold** and line breaks
        const lines = text.split('\n');
        
        return lines.map((line, lIdx) => {
            if (!line.trim()) return <Box key={lIdx} sx={{ height: 8 }} />;

            const trimmedLine = line.trim();
            // Detect headers (1. 2. 3. or - )
            const isListItem = trimmedLine.startsWith('- ');
            const numberedMatch = trimmedLine.match(/^(\d+\.)\s/);
            const isNumberedItem = !!numberedMatch;
            const isMainTip = trimmedLine.startsWith('🐱');

            // Clean the line content from indicators
            let displayLine = line;
            if (isListItem) displayLine = line.replace(/^\s*-\s/, '');
            else if (isNumberedItem) displayLine = line.replace(/^\s*\d+\.\s/, '');

            // Replace **bold**
            const parts = displayLine.split(/(\*\*.*?\*\*)/g);
            const renderedLine = parts.map((part, pIdx) => {
                if (part.startsWith('**') && part.endsWith('**')) {
                    return (
                        <Box component="span" key={pIdx} sx={{ fontWeight: 800, color: 'secondary.main' }}>
                            {part.substring(2, part.length - 2)}
                        </Box>
                    );
                }
                return part;
            });

            return (
                <Typography 
                    key={lIdx} 
                    variant={isMainTip ? "h6" : "body1"}
                    sx={{ 
                        mb: 1.5, 
                        lineHeight: 1.7,
                        pl: (isListItem || isNumberedItem) ? 4 : 0,
                        position: 'relative',
                        fontSize: isMainTip ? '1.1rem' : '1rem',
                        fontWeight: isMainTip ? 600 : 400,
                        color: isDark ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.8)',
                        '&:before': isListItem ? {
                            content: '"•"',
                            position: 'absolute',
                            left: 12,
                            color: 'secondary.main',
                            fontWeight: 'bold',
                            fontSize: '1.2rem'
                        } : isNumberedItem ? {
                            content: `"${numberedMatch![1]}"`,
                            position: 'absolute',
                            left: 0,
                            color: 'secondary.main',
                            fontWeight: 800,
                            fontSize: '0.9rem',
                            width: '28px',
                            textAlign: 'right'
                        } : {}
                    }}
                >
                    {renderedLine}
                </Typography>
            );
        });
    };

    const parts = parseContent(content);

    const getPriorityColor = (priority: any) => {
        const p = String(priority || 'medium').toLowerCase();
        if (p === 'high' || p === 'urgent') return theme.palette.error.main;
        if (p === 'medium') return theme.palette.warning.main;
        return theme.palette.success.main;
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {parts.map((part: any, index: number) => {
                if (part.type === 'text') {
                    return <Box key={index}>{renderText(part.content || '')}</Box>;
                } else if (part.type === 'command') {
                    if (part.command === 'CREATE_TASK') {
                        const pColor = getPriorityColor(part.data?.priority);
                        return (
                            <Paper
                                key={index}
                                elevation={0}
                                sx={{
                                    p: 2.5,
                                    my: 2,
                                    borderRadius: '20px',
                                    bgcolor: isDark ? alpha(pColor, 0.1) : alpha(pColor, 0.05),
                                    border: `1px solid ${alpha(pColor, 0.3)}`,
                                    borderLeft: `6px solid ${pColor}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 2,
                                    boxShadow: `0 4px 20px ${alpha(pColor, 0.1)}`
                                }}
                            >
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Box sx={{ 
                                        p: 1.2, 
                                        bgcolor: pColor, 
                                        color: 'white', 
                                        borderRadius: '14px',
                                        display: 'flex',
                                        boxShadow: `0 4px 10px ${alpha(pColor, 0.3)}`
                                    }}>
                                        <AddTaskIcon />
                                    </Box>
                                    <Box>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: isDark ? 'white' : 'text.primary', mb: 0.2 }}>
                                            {part.data.title}
                                        </Typography>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Chip 
                                                label={part.data.priority?.toUpperCase()} 
                                                size="small" 
                                                sx={{ 
                                                    height: 18, 
                                                    fontSize: '0.65rem', 
                                                    fontWeight: 900, 
                                                    bgcolor: pColor, 
                                                    color: 'white' 
                                                }} 
                                            />
                                            <Typography variant="caption" color="text.secondary">
                                                Acción sugerida
                                            </Typography>
                                        </Stack>
                                    </Box>
                                </Stack>
                                <Button 
                                    variant="contained" 
                                    size="medium" 
                                    startIcon={<ActionIcon />}
                                    onClick={() => onApplyAction?.(part.command, part.data)}
                                    sx={{ 
                                        borderRadius: '12px',
                                        textTransform: 'none',
                                        fontWeight: 800,
                                        bgcolor: pColor,
                                        '&:hover': { bgcolor: alpha(pColor, 0.8) }
                                    }}
                                >
                                    Crear Tarea
                                </Button>
                            </Paper>
                        );
                    }
                }
                return null;
            })}
        </Box>
    );
};

export default LynaInsightRenderer;
